/**
 * A minimal WebM (Matroska subset) muxer and parser (Phase 4B). No dependency.
 *
 * Muxer layout (everything the player needs is at the front, so the file is seekable):
 *
 *     EBML header
 *     Segment (known size)
 *       SeekHead → Info, Tracks, Cues
 *       Info      TimecodeScale 1 ms, Duration
 *       Tracks    1: video (V_VP8 / V_VP9), 2: audio (A_OPUS, OpusHead, CodecDelay, SeekPreRoll)
 *       Cues      one cue per cluster that starts on a video key frame
 *       Cluster*  Timecode + SimpleBlocks in timestamp order (audio interleaved)
 *
 * Clusters are finished into Blobs as soon as they close, so encoded data is never held in
 * one growing buffer. Positions in Cues and SeekHead are written as fixed 8-byte integers,
 * which makes their size independent of their values (they precede the clusters).
 *
 * The parser reads files written here and by MediaRecorder (unknown-size Segment and
 * Clusters, SimpleBlock or BlockGroup). It is used to re-mux real-time recordings (adding
 * duration and cues) and by the tests to inspect exported files.
 */

export const ID = {
  EBML: 0x1a45dfa3,
  EBMLVersion: 0x4286,
  EBMLReadVersion: 0x42f7,
  EBMLMaxIDLength: 0x42f2,
  EBMLMaxSizeLength: 0x42f3,
  DocType: 0x4282,
  DocTypeVersion: 0x4287,
  DocTypeReadVersion: 0x4285,
  Segment: 0x18538067,
  SeekHead: 0x114d9b74,
  Seek: 0x4dbb,
  SeekID: 0x53ab,
  SeekPosition: 0x53ac,
  Info: 0x1549a966,
  TimecodeScale: 0x2ad7b1,
  Duration: 0x4489,
  MuxingApp: 0x4d80,
  WritingApp: 0x5741,
  Tracks: 0x1654ae6b,
  TrackEntry: 0xae,
  TrackNumber: 0xd7,
  TrackUID: 0x73c5,
  TrackType: 0x83,
  FlagLacing: 0x9c,
  CodecID: 0x86,
  CodecPrivate: 0x63a2,
  CodecDelay: 0x56aa,
  SeekPreRoll: 0x56bb,
  DefaultDuration: 0x23e383,
  Video: 0xe0,
  PixelWidth: 0xb0,
  PixelHeight: 0xba,
  Audio: 0xe1,
  SamplingFrequency: 0xb5,
  Channels: 0x9f,
  Cues: 0x1c53bb6b,
  CuePoint: 0xbb,
  CueTime: 0xb3,
  CueTrackPositions: 0xb7,
  CueTrack: 0xf7,
  CueClusterPosition: 0xf1,
  Cluster: 0x1f43b675,
  Timecode: 0xe7,
  SimpleBlock: 0xa3,
  BlockGroup: 0xa0,
  Block: 0xa1,
  ReferenceBlock: 0xfb,
  Void: 0xec,
  Tags: 0x1254c367,
} as const;

const MASTER = new Set<number>([ID.EBML, ID.Segment, ID.SeekHead, ID.Seek, ID.Info, ID.Tracks, ID.TrackEntry, ID.Video, ID.Audio, ID.Cues, ID.CuePoint, ID.CueTrackPositions, ID.Cluster, ID.BlockGroup]);
const TOP_LEVEL = new Set<number>([ID.SeekHead, ID.Info, ID.Tracks, ID.Cues, ID.Cluster, ID.Tags]);

// ------------------------------------------------------------------ encoding

type Bytes = Uint8Array<ArrayBuffer>;

function idBytes(id: number): Bytes {
  const out: number[] = [];
  for (let v = id; v > 0; v = Math.floor(v / 256)) out.unshift(v & 0xff);
  return new Uint8Array(out);
}

/** EBML variable-length size (minimal length). */
export function vint(size: number): Bytes {
  for (let len = 1; len <= 8; len++) {
    // 2^(7·len) − 1 is reserved ("unknown size").
    if (size < 2 ** (7 * len) - 1) {
      const out = new Uint8Array(len);
      let v = size;
      for (let i = len - 1; i >= 0; i--) {
        out[i] = v & 0xff;
        v = Math.floor(v / 256);
      }
      out[0]! |= 1 << (8 - len);
      return out;
    }
  }
  throw new RangeError(`EBML size too large: ${size}`);
}

function uintBytes(value: number, fixed?: number): Bytes {
  if (!Number.isSafeInteger(value) || value < 0) throw new RangeError(`Not an unsigned integer: ${value}`);
  const out: number[] = [];
  for (let v = value; v > 0; v = Math.floor(v / 256)) out.unshift(v & 0xff);
  if (out.length === 0) out.push(0);
  if (fixed) {
    if (out.length > fixed) throw new RangeError(`${value} does not fit in ${fixed} bytes`);
    while (out.length < fixed) out.unshift(0);
  }
  return new Uint8Array(out);
}

function floatBytes(value: number): Bytes {
  const out = new Uint8Array(8);
  new DataView(out.buffer).setFloat64(0, value);
  return out;
}

const text = (s: string): Bytes => Uint8Array.from(s, (c) => c.charCodeAt(0) & 0x7f);

function concat(parts: readonly Uint8Array[]): Bytes {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

const el = (id: number, payload: Uint8Array | readonly Uint8Array[]): Bytes => {
  const body = payload instanceof Uint8Array ? payload : concat(payload);
  return concat([idBytes(id), vint(body.length), body]);
};
const uint = (id: number, v: number, fixed?: number) => el(id, uintBytes(v, fixed));
const float = (id: number, v: number) => el(id, floatBytes(v));
const str = (id: number, s: string) => el(id, text(s));

// ------------------------------------------------------------------ muxer

export interface MuxVideoTrack {
  codec: 'vp8' | 'vp9';
  width: number;
  height: number;
  fps: number;
}

export interface MuxAudioTrack {
  /** Opus identification header (OpusHead), from the encoder's decoderConfig.description. */
  opusHead: Uint8Array;
  sampleRate: number;
  channels: number;
}

interface Packet {
  data: Uint8Array;
  timestampUs: number;
}

/** Pre-skip (encoder delay, in 48 kHz samples) of an OpusHead. */
export function opusPreSkip(head: Uint8Array): number {
  if (head.length < 19 || text('OpusHead').some((b, i) => head[i] !== b)) throw new Error('Invalid OpusHead.');
  return head[10]! | (head[11]! << 8);
}

/** A standard OpusHead (RFC 7845) for encoders that do not provide one. */
export function makeOpusHead(channels: number, sampleRate: number, preSkip: number): Bytes {
  const out = new Uint8Array(19);
  out.set(text('OpusHead'), 0);
  const dv = new DataView(out.buffer);
  dv.setUint8(8, 1);
  dv.setUint8(9, channels);
  dv.setUint16(10, preSkip, true);
  dv.setUint32(12, sampleRate, true);
  return out;
}

export interface MuxStats {
  clusters: number;
  videoBlocks: number;
  audioBlocks: number;
  keyFrames: number;
}

const MAX_CLUSTER_SPAN_MS = 30_000;

/**
 * Builds a WebM file. Add every audio packet first (audio is encoded before video), then
 * video chunks in presentation order; audio is interleaved into the clusters by time.
 */
export class WebmMuxer {
  private readonly audioQueue: Packet[] = [];
  private audioNext = 0;
  private clusters: Blob[] = [];
  private cues: { timeMs: number; offset: number }[] = [];
  private offset = 0;
  private cluster: { timeMs: number; parts: Uint8Array[]; bytes: number; key: boolean } | null = null;
  private lastVideoMs = -1;
  private finished = false;
  readonly stats: MuxStats = { clusters: 0, videoBlocks: 0, audioBlocks: 0, keyFrames: 0 };

  constructor(
    private readonly video: MuxVideoTrack,
    private readonly audio: MuxAudioTrack | null,
  ) {}

  addAudio(data: Uint8Array, timestampUs: number): void {
    if (!this.audio) throw new Error('This file has no audio track.');
    const last = this.audioQueue[this.audioQueue.length - 1];
    if (last && timestampUs < last.timestampUs) throw new Error('Audio packets must be added in order.');
    this.audioQueue.push({ data, timestampUs });
  }

  addVideo(data: Uint8Array, timestampUs: number, key: boolean): void {
    if (this.finished) throw new Error('The file is already finished.');
    const ms = Math.round(timestampUs / 1000);
    if (ms < this.lastVideoMs) throw new Error('Video chunks must be added in presentation order.');
    if (!this.cluster && !key) throw new Error('The first video chunk must be a key frame.');
    // Audio before this frame goes first, in the current cluster.
    this.flushAudio(ms, false);
    if (!this.cluster || (key && ms > this.cluster.timeMs) || ms - this.cluster.timeMs > MAX_CLUSTER_SPAN_MS) {
      this.closeCluster();
      this.openCluster(ms, key);
    }
    this.block(1, ms, key, data);
    this.lastVideoMs = ms;
    this.stats.videoBlocks++;
    if (key) this.stats.keyFrames++;
  }

  /** Finishes the file; `durationMs` is written as the segment duration. */
  finish(durationMs: number): Blob {
    if (this.finished) throw new Error('The file is already finished.');
    if (!this.cluster) throw new Error('No video was added.');
    this.flushAudio(Infinity, true);
    this.closeCluster();
    this.finished = true;
    const head = this.header(durationMs);
    const blob = new Blob([...head, ...this.clusters], { type: this.audio ? `video/webm;codecs=${this.video.codec},opus` : `video/webm;codecs=${this.video.codec}` });
    this.clusters = [];
    return blob;
  }

  private flushAudio(beforeMs: number, all: boolean): void {
    while (this.audioNext < this.audioQueue.length) {
      const p = this.audioQueue[this.audioNext]!;
      const ms = Math.round(p.timestampUs / 1000);
      if (!all && ms >= beforeMs) break;
      if (!this.cluster) break; // no cluster yet: the first (key) video frame opens it
      if (ms - this.cluster.timeMs > MAX_CLUSTER_SPAN_MS) {
        this.closeCluster();
        this.openCluster(ms, false);
      }
      this.block(2, Math.max(ms, this.cluster!.timeMs), true, p.data);
      this.audioQueue[this.audioNext] = { data: new Uint8Array(0), timestampUs: p.timestampUs }; // release
      this.audioNext++;
      this.stats.audioBlocks++;
    }
  }

  private openCluster(timeMs: number, key: boolean): void {
    const tc = uint(ID.Timecode, timeMs);
    this.cluster = { timeMs, parts: [tc], bytes: tc.length, key };
  }

  private block(track: number, ms: number, key: boolean, data: Uint8Array): void {
    const c = this.cluster!;
    const rel = ms - c.timeMs;
    const head = new Uint8Array(4);
    head[0] = 0x80 | track;
    new DataView(head.buffer).setInt16(1, rel);
    head[3] = key ? 0x80 : 0;
    const size = head.length + data.length;
    const prefix = concat([idBytes(ID.SimpleBlock), vint(size), head]);
    c.parts.push(prefix, data);
    c.bytes += prefix.length + data.length;
  }

  private closeCluster(): void {
    const c = this.cluster;
    if (!c) return;
    this.cluster = null;
    if (c.key) this.cues.push({ timeMs: c.timeMs, offset: this.offset });
    const prefix = concat([idBytes(ID.Cluster), vint(c.bytes)]);
    this.clusters.push(new Blob([prefix, ...(c.parts as BlobPart[])]));
    this.offset += prefix.length + c.bytes;
    this.stats.clusters++;
  }

  private header(durationMs: number): Bytes[] {
    const ebml = el(ID.EBML, [
      uint(ID.EBMLVersion, 1),
      uint(ID.EBMLReadVersion, 1),
      uint(ID.EBMLMaxIDLength, 4),
      uint(ID.EBMLMaxSizeLength, 8),
      str(ID.DocType, 'webm'),
      uint(ID.DocTypeVersion, 4),
      uint(ID.DocTypeReadVersion, 2),
    ]);
    const info = el(ID.Info, [uint(ID.TimecodeScale, 1_000_000), str(ID.MuxingApp, 'SonicForge'), str(ID.WritingApp, 'SonicForge Studio'), float(ID.Duration, durationMs)]);
    const v = this.video;
    const tracks = [
      el(ID.TrackEntry, [
        uint(ID.TrackNumber, 1),
        uint(ID.TrackUID, 1),
        uint(ID.TrackType, 1),
        uint(ID.FlagLacing, 0),
        str(ID.CodecID, v.codec === 'vp9' ? 'V_VP9' : 'V_VP8'),
        uint(ID.DefaultDuration, Math.round(1e9 / v.fps)),
        el(ID.Video, [uint(ID.PixelWidth, v.width), uint(ID.PixelHeight, v.height)]),
      ]),
    ];
    if (this.audio) {
      const a = this.audio;
      tracks.push(
        el(ID.TrackEntry, [
          uint(ID.TrackNumber, 2),
          uint(ID.TrackUID, 2),
          uint(ID.TrackType, 2),
          uint(ID.FlagLacing, 0),
          str(ID.CodecID, 'A_OPUS'),
          el(ID.CodecPrivate, a.opusHead),
          // Pre-skip in nanoseconds: the decoder discards these samples, so audio sample 0 is t = 0.
          uint(ID.CodecDelay, Math.round((opusPreSkip(a.opusHead) / 48000) * 1e9)),
          uint(ID.SeekPreRoll, 80_000_000),
          el(ID.Audio, [float(ID.SamplingFrequency, a.sampleRate), uint(ID.Channels, a.channels)]),
        ]),
      );
    }
    const tracksEl = el(ID.Tracks, tracks);
    // Sizes of SeekHead and Cues do not depend on positions (fixed 8-byte integers).
    const seekHead = (positions: number[]) =>
      el(
        ID.SeekHead,
        [ID.Info, ID.Tracks, ID.Cues].map((id, i) => el(ID.Seek, [el(ID.SeekID, idBytes(id)), uint(ID.SeekPosition, positions[i]!, 8)])),
      );
    const cuesOf = (base: number) =>
      el(
        ID.Cues,
        this.cues.map((c) => el(ID.CuePoint, [uint(ID.CueTime, c.timeMs), el(ID.CueTrackPositions, [uint(ID.CueTrack, 1), uint(ID.CueClusterPosition, base + c.offset, 8)])])),
      );
    const seekLen = seekHead([0, 0, 0]).length;
    const infoAt = seekLen;
    const tracksAt = infoAt + info.length;
    const cuesAt = tracksAt + tracksEl.length;
    const cuesLen = cuesOf(0).length;
    const clustersAt = cuesAt + cuesLen;
    const front = [seekHead([infoAt, tracksAt, cuesAt]), info, tracksEl, cuesOf(clustersAt)];
    const segmentSize = clustersAt + this.offset;
    return [ebml, concat([idBytes(ID.Segment), vint(segmentSize)]), ...front];
  }
}

// ------------------------------------------------------------------ parser

export interface ParsedTrack {
  number: number;
  type: number;
  codecId: string;
  codecPrivate: Uint8Array | null;
  codecDelayNs: number;
  defaultDurationNs: number;
  width: number;
  height: number;
  sampleRate: number;
  channels: number;
}

export interface ParsedBlock {
  track: number;
  /** Absolute time in timecode units (ms with the default scale). */
  time: number;
  key: boolean;
  data: Uint8Array;
}

export interface ParsedWebm {
  docType: string;
  timecodeScale: number;
  /** Segment duration in timecode units, or null when absent (MediaRecorder). */
  duration: number | null;
  tracks: ParsedTrack[];
  blocks: ParsedBlock[];
  cues: { time: number; track: number; clusterPosition: number }[];
  /** Byte offset of the segment's data (cue positions are relative to it). */
  segmentDataOffset: number;
  /** Byte offsets of clusters, relative to the segment data. */
  clusterPositions: number[];
  /** Known sizes that ran past the end of the file (a truncated or corrupt file). */
  truncated: boolean;
}

const UNKNOWN = -1;

function readVint(b: Uint8Array, at: number, keepMarker: boolean): { value: number; length: number } {
  const first = b[at];
  if (first === undefined) throw new RangeError('Unexpected end of data');
  let len = 1;
  while (len <= 8 && !(first & (0x80 >> (len - 1)))) len++;
  if (len > 8) throw new RangeError(`Invalid EBML number at ${at}`);
  if (at + len > b.length) throw new RangeError('Unexpected end of data');
  let value = keepMarker ? first : first & (0xff >> len);
  let allOnes = value === (0xff >> len);
  for (let i = 1; i < len; i++) {
    value = value * 256 + b[at + i]!;
    if (b[at + i] !== 0xff) allOnes = false;
  }
  return { value: !keepMarker && allOnes ? UNKNOWN : value, length: len };
}

const readUint = (b: Uint8Array, at: number, len: number) => {
  let v = 0;
  for (let i = 0; i < len; i++) v = v * 256 + b[at + i]!;
  return v;
};
const readFloat = (b: Uint8Array, at: number, len: number) => {
  const dv = new DataView(b.buffer, b.byteOffset + at, len);
  return len === 4 ? dv.getFloat32(0) : dv.getFloat64(0);
};
/** ASCII strings (DocType, CodecID): decoded byte by byte, independent of TextDecoder realms. */
const readText = (b: Uint8Array, at: number, len: number) => {
  let s = '';
  for (let i = at; i < at + len && b[i] !== 0; i++) s += String.fromCharCode(b[i]!);
  return s;
};

export function parseWebm(bytes: Uint8Array): ParsedWebm {
  const out: ParsedWebm = { docType: '', timecodeScale: 1_000_000, duration: null, tracks: [], blocks: [], cues: [], segmentDataOffset: 0, clusterPositions: [], truncated: false };
  let clusterTime = 0;
  let track: ParsedTrack | null = null;
  let cue: { time: number; track: number; clusterPosition: number } | null = null;
  let groupBlock: ParsedBlock | null = null;
  let groupReferenced = false;

  const leaf = (id: number, at: number, len: number) => {
    switch (id) {
      case ID.DocType:
        out.docType = readText(bytes, at, len);
        break;
      case ID.TimecodeScale:
        out.timecodeScale = readUint(bytes, at, len);
        break;
      case ID.Duration:
        out.duration = readFloat(bytes, at, len);
        break;
      case ID.Timecode:
        clusterTime = readUint(bytes, at, len);
        break;
      case ID.SimpleBlock:
      case ID.Block: {
        const tn = readVint(bytes, at, false);
        const rel = new DataView(bytes.buffer, bytes.byteOffset + at + tn.length, 2).getInt16(0);
        const flags = bytes[at + tn.length + 2]!;
        const data = bytes.subarray(at + tn.length + 3, at + len);
        // A Block (in a BlockGroup) is a key frame unless the group has a ReferenceBlock.
        out.blocks.push({ track: tn.value, time: clusterTime + rel, key: id === ID.SimpleBlock ? (flags & 0x80) !== 0 : true, data });
        if (id === ID.Block) groupBlock = out.blocks[out.blocks.length - 1]!;
        break;
      }
      case ID.ReferenceBlock:
        if (groupBlock) groupBlock.key = false;
        else groupReferenced = true;
        break;
      default:
        if (track) {
          if (id === ID.TrackNumber) track.number = readUint(bytes, at, len);
          else if (id === ID.TrackType) track.type = readUint(bytes, at, len);
          else if (id === ID.CodecID) track.codecId = readText(bytes, at, len);
          else if (id === ID.CodecPrivate) track.codecPrivate = bytes.slice(at, at + len);
          else if (id === ID.CodecDelay) track.codecDelayNs = readUint(bytes, at, len);
          else if (id === ID.DefaultDuration) track.defaultDurationNs = readUint(bytes, at, len);
          else if (id === ID.PixelWidth) track.width = readUint(bytes, at, len);
          else if (id === ID.PixelHeight) track.height = readUint(bytes, at, len);
          else if (id === ID.SamplingFrequency) track.sampleRate = readFloat(bytes, at, len);
          else if (id === ID.Channels) track.channels = readUint(bytes, at, len);
        }
        if (cue) {
          if (id === ID.CueTime) cue.time = readUint(bytes, at, len);
          else if (id === ID.CueTrack) cue.track = readUint(bytes, at, len);
          else if (id === ID.CueClusterPosition) cue.clusterPosition = readUint(bytes, at, len);
        }
    }
  };

  // Iterative walk; an unknown-size master ends at the end of its parent or at the next top-level element.
  const walk = (start: number, end: number, depth: number, parent: number) => {
    let at = start;
    while (at < end) {
      const idv = readVint(bytes, at, true);
      const id = idv.value;
      if (parent !== ID.Segment && depth > 0 && (TOP_LEVEL.has(id) || id === ID.Segment) && parentIsUnknown) return at;
      const sz = readVint(bytes, at + idv.length, false);
      const dataAt = at + idv.length + sz.length;
      const known = sz.value !== UNKNOWN;
      let dataEnd = known ? dataAt + sz.value : end;
      if (dataEnd > bytes.length) {
        out.truncated = true;
        dataEnd = bytes.length;
      }
      if (MASTER.has(id)) {
        if (id === ID.Segment) out.segmentDataOffset = dataAt;
        if (id === ID.Cluster) out.clusterPositions.push(at - out.segmentDataOffset);
        if (id === ID.TrackEntry) track = { number: 0, type: 0, codecId: '', codecPrivate: null, codecDelayNs: 0, defaultDurationNs: 0, width: 0, height: 0, sampleRate: 0, channels: 0 };
        if (id === ID.CuePoint) cue = { time: 0, track: 0, clusterPosition: 0 };
        if (id === ID.BlockGroup) {
          groupBlock = null;
          groupReferenced = false;
        }
        const saved = parentIsUnknown;
        parentIsUnknown = !known;
        const stop = walk(dataAt, dataEnd, depth + 1, id);
        parentIsUnknown = saved;
        if (id === ID.TrackEntry && track) {
          out.tracks.push(track);
          track = null;
        }
        if (id === ID.BlockGroup) {
          // A ReferenceBlock written before the Block.
          if (groupBlock && groupReferenced) groupBlock.key = false;
          groupBlock = null;
        }
        if (id === ID.CuePoint && cue) {
          out.cues.push(cue);
          cue = null;
        }
        at = known ? dataEnd : stop;
      } else {
        if (dataEnd - dataAt === (known ? sz.value : -2)) leaf(id, dataAt, sz.value);
        else out.truncated = true;
        at = dataEnd;
      }
    }
    return Math.min(at, end);
  };
  let parentIsUnknown = false;
  walk(0, bytes.length, 0, 0);
  return out;
}
